import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import styles from "../../styles/pages/games/QuickMath.module.scss";

const OPS_BY_LEVEL = {
  1: ["+"],
  2: ["+", "−"],
  3: ["+", "−", "×"],
  6: ["+", "−", "×", "÷"],
};

function pickOps(level) {
  if (level >= 6) return OPS_BY_LEVEL[6];
  if (level >= 3) return OPS_BY_LEVEL[3];
  if (level >= 2) return OPS_BY_LEVEL[2];
  return OPS_BY_LEVEL[1];
}

function rndInt(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function clamp(v, min, max) {
  return Math.min(max, Math.max(min, v));
}

function makeQuestion(level) {
  const ops = pickOps(level);
  const op = ops[(Math.random() * ops.length) | 0];

  const max = Math.min(30, 7 + level * 3);

  let a = rndInt(1, max);
  let b = rndInt(1, max);
  let ans = 0;

  if (op === "+") ans = a + b;

  if (op === "−") {
    if (level < 4 && b > a) [a, b] = [b, a];
    ans = a - b;
  }

  if (op === "×") {
    const m = Math.min(12, 4 + level);
    a = rndInt(2, m);
    b = rndInt(2, m);
    ans = a * b;
  }

  if (op === "÷") {
    const m = Math.min(12, 4 + level);
    b = rndInt(2, m);
    ans = rndInt(2, m);
    a = b * ans;
  }

  return { a, b, op, ans };
}

function makeChoices(correct, level) {
  const spread = Math.max(3, Math.min(12, 2 + Math.floor(level * 1.2)));
  const set = new Set([correct]);

  while (set.size < 4) {
    const roll = Math.random();
    let d;

    if (roll < 0.55) d = correct + rndInt(-spread, spread);
    else if (roll < 0.8) d = correct + rndInt(-spread * 2, spread * 2);
    else d = -correct + rndInt(-2, 2);

    if (d === correct) continue;
    set.add(d);
  }

  const arr = Array.from(set);
  for (let i = arr.length - 1; i > 0; i--) {
    const j = (Math.random() * (i + 1)) | 0;
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

/** small WebAudio SFX (no assets) */
function useSfx() {
  const ctxRef = useRef(null);
  const masterRef = useRef(null);
  const enabledRef = useRef(false);

  const ensure = useCallback(async () => {
    if (!enabledRef.current) return false;
    try {
      if (!ctxRef.current) {
        ctxRef.current = new (window.AudioContext || window.webkitAudioContext)();
        masterRef.current = ctxRef.current.createGain();
        masterRef.current.gain.value = 0.18;
        masterRef.current.connect(ctxRef.current.destination);
      }
      if (ctxRef.current.state === "suspended") await ctxRef.current.resume();
      return true;
    } catch {
      return false;
    }
  }, []);

  const tone = useCallback(
    async (f, d = 0.08, type = "triangle", gain = 1) => {
      if (!(await ensure())) return;
      const ctx = ctxRef.current;
      const master = masterRef.current;
      if (!ctx || !master) return;

      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = type;
      o.frequency.value = f;

      o.connect(g);
      g.connect(master);

      const t = ctx.currentTime;
      g.gain.setValueAtTime(0.0001, t);
      g.gain.linearRampToValueAtTime(gain, t + 0.01);
      g.gain.exponentialRampToValueAtTime(0.0001, t + d);

      o.start(t);
      o.stop(t + d + 0.02);
    },
    [ensure]
  );

  const seq = useCallback(
    async (notes) => {
      if (!(await ensure())) return;
      for (const n of notes) {
        // simple "fire and forget" scheduling; good enough for UI SFX
        tone(n.f, n.d ?? 0.08, n.type ?? "triangle", n.gain ?? 1);
      }
    },
    [ensure, tone]
  );

  return {
    enable: async () => {
      enabledRef.current = true;
      await ensure();
      seq([{ f: 660, d: 0.06 }, { f: 880, d: 0.07 }]);
    },
    disable: () => {
      enabledRef.current = false;
    },
    isOn: () => enabledRef.current,
    correct: () => seq([{ f: 740, d: 0.06 }, { f: 980, d: 0.08 }]),
    wrong: () => seq([{ f: 210, d: 0.09, type: "square" }, { f: 160, d: 0.12, type: "square" }]),
    levelUp: () => seq([{ f: 523, d: 0.07 }, { f: 659, d: 0.07 }, { f: 784, d: 0.10 }]),
    tick: () => tone(440, 0.04, "triangle", 0.6),
    finish: () => seq([{ f: 392, d: 0.10 }, { f: 330, d: 0.12 }, { f: 262, d: 0.14 }]),
  };
}

/** Auto place start level from last run */
function autoPlaceLevel({ score, accuracy, best }) {
  const base = Math.floor(score / 35) + 1; // ~35 pts per level
  const accAdj = accuracy >= 90 ? 2 : accuracy >= 80 ? 1 : accuracy < 55 ? -1 : 0;
  const bestAdj = Math.floor(best / 120); // gentle long-term push
  return clamp(base + accAdj + bestAdj, 1, 10);
}

function manualLevelFromDifficulty(d) {
  if (d === "low") return 1;
  if (d === "med") return 4;
  if (d === "high") return 7;
  return 1;
}

function Icon({ className, fallback }) {
  // If FA isn't loaded, show emoji fallback
  return <i className={className} aria-hidden>{/* FA */}</i> || <span aria-hidden>{fallback}</span>;
}

export default function QuickMath() {
  const ROUND_SECONDS = 30;

  const sfx = useSfx();

  const [time, setTime] = useState(ROUND_SECONDS);
  const [running, setRunning] = useState(true);

  const [difficulty, setDifficulty] = useState(() => localStorage.getItem("qm_difficulty") || "auto");
  const [startLevel, setStartLevel] = useState(() => Number(localStorage.getItem("qm_startLevel") || 1));

  const [level, setLevel] = useState(() => {
    const chosen = localStorage.getItem("qm_difficulty") || "auto";
    const storedStart = Number(localStorage.getItem("qm_startLevel") || 1);
    return chosen === "auto" ? storedStart : manualLevelFromDifficulty(chosen);
  });

  const [q, setQ] = useState(() => makeQuestion(level));
  const [choices, setChoices] = useState(() => makeChoices(q.ans, level));

  const [score, setScore] = useState(0);
  const [best, setBest] = useState(Number(localStorage.getItem("qm_best") || 0));

  const [streak, setStreak] = useState(0);
  const [bestStreak, setBestStreak] = useState(0);
  const [correctCount, setCorrectCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);

  const [flash, setFlash] = useState(""); // "good" | "bad" | ""
  const [lastDelta, setLastDelta] = useState(null);

  const qStartRef = useRef(performance.now());
  const startBandRef = useRef({ base: level, min: clamp(level - 2, 1, 10), max: clamp(level + 2, 1, 10) });

  const accuracy = useMemo(() => {
    if (totalCount === 0) return 0;
    return Math.round((correctCount / totalCount) * 100);
  }, [correctCount, totalCount]);

  const startNew = useCallback((nextLevel) => {
    const nq = makeQuestion(nextLevel);
    setQ(nq);
    setChoices(makeChoices(nq.ans, nextLevel));
    qStartRef.current = performance.now();
  }, []);

  // Timer
  useEffect(() => {
    if (!running) return;

    const id = setInterval(() => {
      setTime((t) => {
        if (t <= 1) {
          setRunning(false);
          return 0;
        }
        return t - 1;
      });
    }, 1000);

    return () => clearInterval(id);
  }, [running]);

  // Countdown ticks
  useEffect(() => {
    if (!running) return;
    if (time <= 5 && time > 0) sfx.tick();
    if (time === 0) sfx.finish();
  }, [time, running, sfx]);

  // Save best + auto place for next run
  useEffect(() => {
    if (running) return;

    setBest((b) => {
      const nextBest = Math.max(b, score);
      localStorage.setItem("qm_best", String(nextBest));

      if (difficulty === "auto") {
        const nextStart = autoPlaceLevel({ score, accuracy, best: nextBest });
        setStartLevel(nextStart);
        localStorage.setItem("qm_startLevel", String(nextStart));
      }
      return nextBest;
    });
  }, [running, score, accuracy, difficulty]);

  const submitAnswer = useCallback(
    (val) => {
      if (!running) return;

      const rt = performance.now() - qStartRef.current;
      const isCorrect = val === q.ans;

      setTotalCount((n) => n + 1);

      if (isCorrect) {
        setCorrectCount((n) => n + 1);
        setFlash("good");
        sfx.correct();

        const speedBonus = rt < 1200 ? 3 : rt < 2000 ? 2 : rt < 2800 ? 1 : 0;
        const base = 6 + Math.min(10, level);
        const streakBonus = Math.min(6, Math.floor(streak / 3));
        const gained = base + speedBonus + streakBonus;

        setScore((s) => s + gained);
        setLastDelta(`+${gained}`);

        setStreak((s) => {
          const next = s + 1;
          setBestStreak((bs) => Math.max(bs, next));
          return next;
        });

        setTime((t) => Math.min(60, t + 1));

        // within-run adaptation, clamped to ±2 from start band
        let nextLevel = level;
        const shouldLevelUp = rt < 2300 && (streak + 1) % 3 === 0;
        if (shouldLevelUp) nextLevel = Math.min(10, level + 1);

        nextLevel = clamp(nextLevel, startBandRef.current.min, startBandRef.current.max);

        if (nextLevel !== level) sfx.levelUp();
        setLevel(nextLevel);
        startNew(nextLevel);
      } else {
        setFlash("bad");
        sfx.wrong();
        setLastDelta("−2s");

        setStreak(0);
        setTime((t) => Math.max(0, t - 2));

        let nextLevel = level;
        if (level > 3) nextLevel = level - 1;

        nextLevel = clamp(nextLevel, startBandRef.current.min, startBandRef.current.max);

        setLevel(nextLevel);
        startNew(nextLevel);
      }

      window.setTimeout(() => setFlash(""), 140);
    },
    [running, q.ans, level, streak, sfx, startNew]
  );

  // Keyboard 1–4 selects
  useEffect(() => {
    const onKey = (e) => {
      if (!running) return;
      if (e.key >= "1" && e.key <= "4") {
        const idx = Number(e.key) - 1;
        if (choices[idx] !== undefined) submitAnswer(choices[idx]);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [choices, running, submitAnswer]);

  const applyDifficulty = useCallback(
    (d) => {
      setDifficulty(d);
      localStorage.setItem("qm_difficulty", d);

      const chosen = d === "auto" ? startLevel : manualLevelFromDifficulty(d);
      setLevel(chosen);
      startBandRef.current = { base: chosen, min: clamp(chosen - 2, 1, 10), max: clamp(chosen + 2, 1, 10) };

      // keep startLevel meaningful in manual too (optional)
      setStartLevel(chosen);
      localStorage.setItem("qm_startLevel", String(chosen));
    },
    [startLevel]
  );

  const toggleRun = async () => {
    if (!sfx.isOn()) await sfx.enable();
    setRunning((r) => !r);
  };

  const restart = async () => {
    if (!sfx.isOn()) await sfx.enable();

    const chosen = difficulty === "auto" ? startLevel : manualLevelFromDifficulty(difficulty);

    setTime(ROUND_SECONDS);
    setScore(0);
    setStreak(0);
    setBestStreak(0);
    setCorrectCount(0);
    setTotalCount(0);
    setLastDelta(null);
    setFlash("");

    setLevel(chosen);
    startBandRef.current = { base: chosen, min: clamp(chosen - 2, 1, 10), max: clamp(chosen + 2, 1, 10) };

    setRunning(true);
    startNew(chosen);
  };

  const resumeNoReset = async () => {
    if (!sfx.isOn()) await sfx.enable();
    setRunning(true);
  };

  const endTitle = useMemo(() => {
    if (score >= best && score > 0) return "New Best!";
    return "Time Up";
  }, [score, best]);

  return (
    <div className={styles.wrap}>
      <header className={styles.top}>
        <div className={styles.brand}>
          <div className={styles.title}>Quick Math</div>
          <div className={styles.sub}>Speed • Accuracy • Focus</div>
        </div>

        <div className={styles.hud}>
          <div className={styles.hudChip} title="Time">
            <i className={`fa-solid fa-stopwatch ${styles.hudIcon}`} aria-hidden />
            <b>{time}s</b>
          </div>
          <div className={styles.hudChip} title="Score">
            <i className={`fa-solid fa-star ${styles.hudIcon}`} aria-hidden />
            <b>{score}</b>
          </div>
          <div className={styles.hudChip} title="Accuracy">
            <i className={`fa-solid fa-bullseye ${styles.hudIcon}`} aria-hidden />
            <b>{accuracy}%</b>
          </div>
          <div className={styles.hudChip} title="Level">
            <i className={`fa-solid fa-layer-group ${styles.hudIcon}`} aria-hidden />
            <b>Lv {level}</b>
          </div>
        </div>

        <div className={styles.right}>
          <div className={styles.diff} role="group" aria-label="Difficulty">
            {["auto", "low", "med", "high"].map((d) => (
              <button
                key={d}
                type="button"
                className={`${styles.diffBtn} ${difficulty === d ? styles.diffOn : ""}`}
                onClick={() => applyDifficulty(d)}
              >
                {d.toUpperCase()}
              </button>
            ))}
          </div>

          <div className={styles.actions}>
            <button className={styles.btn} onClick={toggleRun}>
              {running ? "Pause" : "Resume"}
            </button>
            <button className={styles.btn} onClick={restart}>
              Restart
            </button>
            <button
              className={`${styles.btn} ${styles.sound}`}
              onClick={async () => {
                if (sfx.isOn()) sfx.disable();
                else await sfx.enable();
              }}
              title="Sound"
              aria-label="Sound"
            >
              <i className={`fa-solid ${sfx.isOn() ? "fa-volume-high" : "fa-volume-xmark"}`} aria-hidden />
            </button>
          </div>
        </div>
      </header>

      <main className={styles.stage}>
        {running ? (
          <div className={`${styles.card} ${flash ? styles[flash] : ""}`}>
            <div className={styles.rowTop}>
              <div className={styles.streak}>
                <span className={styles.streakDot} />
                STREAK <b>{streak}</b>
              </div>

              <div className={styles.delta} aria-live="polite">
                {lastDelta ? lastDelta : ""}
              </div>
            </div>

            <div className={styles.question}>
              <span>{q.a}</span>
              <span className={styles.op}>{q.op}</span>
              <span>{q.b}</span>
              <span className={styles.eq}>=</span>
              <span className={styles.qmark}>?</span>
            </div>

            <div className={styles.choices}>
              {choices.map((c, i) => (
                <button key={i} className={styles.choiceBtn} onClick={() => submitAnswer(c)} type="button">
                  <span className={styles.choiceKey}>{i + 1}</span>
                  <span className={styles.choiceVal}>{c}</span>
                </button>
              ))}
            </div>

            <div className={styles.footerTip}>
              Keys: <b>1–4</b> • Correct: <b>+pts</b> + <b>+1s</b> • Wrong: <b>−2s</b>
            </div>
          </div>
        ) : (
          <div className={styles.overWrap}>
            <div className={styles.overCard}>
              <div className={styles.overTitle}>{endTitle}</div>
              <div className={styles.overSub}>
                Next start level:{" "}
                <b>{difficulty === "auto" ? startLevel : manualLevelFromDifficulty(difficulty)}</b>{" "}
                <span className={styles.overMuted}>({difficulty.toUpperCase()})</span>
              </div>

              <div className={styles.overGrid}>
                <div className={styles.overStat}>
                  <div className={styles.k}>Score</div>
                  <div className={styles.v}>{score}</div>
                </div>
                <div className={styles.overStat}>
                  <div className={styles.k}>Best</div>
                  <div className={styles.v}>{Math.max(best, score)}</div>
                </div>
                <div className={styles.overStat}>
                  <div className={styles.k}>Accuracy</div>
                  <div className={styles.v}>{accuracy}%</div>
                </div>
                <div className={styles.overStat}>
                  <div className={styles.k}>Best streak</div>
                  <div className={styles.v}>{bestStreak}</div>
                </div>
              </div>

              <div className={styles.overActions}>
                <button className={`${styles.btn} ${styles.primary}`} onClick={restart}>
                  Play again
                </button>
                <button className={styles.btn} onClick={resumeNoReset}>
                  Resume (no reset)
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
